import type { ContributionAsk } from '../types';

const ASK_PHOTOS_PRESET = [1, 3, 5] as const;

interface ContributionAskFieldsProps {
  value: ContributionAsk;
  onChange: (next: ContributionAsk) => void;
}

/**
 * Shared "ask them to help" panel — photo-count stepper, voice/context checkboxes,
 * optional note. Used by both Members.tsx and ShareEventModal.tsx. "Curated over
 * exhaustive": the photo count is always one of a small preset, never a free-form
 * number, so an ask never reads as "upload everything you have."
 */
export function ContributionAskFields({ value, onChange }: ContributionAskFieldsProps) {
  const photosOn = value.askPhotos != null;

  return (
    <div className="space-y-3 bg-indigo-50/60 border border-indigo-100 rounded-xl p-3.5">
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-semibold text-slate-700">Ask for photos</span>
          <button
            type="button"
            onClick={() => onChange({ ...value, askPhotos: photosOn ? null : 3 })}
            className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${photosOn ? 'bg-indigo-600' : 'bg-slate-200'}`}
            aria-pressed={photosOn}
          >
            <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${photosOn ? 'translate-x-4' : 'translate-x-0'}`} />
          </button>
        </div>
        {photosOn && (
          <div className="flex gap-1.5">
            {ASK_PHOTOS_PRESET.map(n => (
              <button
                key={n}
                type="button"
                onClick={() => onChange({ ...value, askPhotos: n })}
                aria-pressed={value.askPhotos === n}
                className={`flex-1 py-1.5 rounded-lg text-sm font-semibold transition-colors border ${
                  value.askPhotos === n
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
                }`}
              >
                {n}
              </button>
            ))}
          </div>
        )}
      </div>

      <label className="flex items-start gap-2.5 cursor-pointer">
        <input
          type="checkbox"
          checked={value.askVoice}
          onChange={e => onChange({ ...value, askVoice: e.target.checked })}
          className="mt-0.5 w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
        />
        <span>
          <span className="block text-xs font-semibold text-slate-700">Also track their voice memory</span>
          <span className="block text-[11px] text-slate-500 mt-0.5">They'll always be gently invited to record one either way — this just adds it to your progress view.</span>
        </span>
      </label>

      <label className="flex items-center gap-2.5 cursor-pointer">
        <input
          type="checkbox"
          checked={value.askContext}
          onChange={e => onChange({ ...value, askContext: e.target.checked })}
          className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
        />
        <span className="text-xs font-semibold text-slate-700">Ask for context or a story</span>
      </label>

      <div>
        <label className="block text-[11px] font-medium text-slate-500 mb-1">Note (optional)</label>
        <input
          type="text"
          value={value.note ?? ''}
          onChange={e => onChange({ ...value, note: e.target.value || null })}
          placeholder="e.g. the ones from the backyard"
          className="w-full text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>
    </div>
  );
}

export function emptyContributionAsk(): ContributionAsk {
  return { askPhotos: 3, askVoice: false, askContext: false, note: null };
}

export function hasAnyAsk(value: ContributionAsk): boolean {
  return value.askPhotos != null || value.askVoice || value.askContext;
}
